import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import {
  Box, Stack, Typography,
} from '@stvgroup/stv-ui-components'
import CloseButton from '../CloseButton'
import { responsiveGlobalPadding } from '../../styles/layout'

const containerSx = {
  width: '100%',
  height: '100%',
  zIndex: 9999,
  py: { xs: 2, md: 3 },
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '1267px',
  mx: 'auto',
  overflow: 'hidden',
  alignSelf: 'flex-start',
}

const headerContainerSx = {
  width: '100%',
  mx: 'auto',
  px: responsiveGlobalPadding,
}

const headerRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  width: '100%',
}

const headerStartSx = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
}

const headerEndSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
}

const contentSx = {
  flex: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  px: responsiveGlobalPadding,
  minHeight: 0,
}

const ModalLayoutRoot = forwardRef(({
  children, sx, disableFlex, ...props
}, ref) => {
  const finalSx = { ...containerSx, ...sx }

  if (disableFlex) {
    delete finalSx.display
    delete finalSx.flexDirection
  }

  return (
    <Box ref={ref} sx={finalSx} {...props}>
      {children}
    </Box>
  )
})

ModalLayoutRoot.displayName = 'ModalLayout'

ModalLayoutRoot.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
  disableFlex: PropTypes.bool,
}

ModalLayoutRoot.defaultProps = {
  children: null,
  sx: {},
  disableFlex: false,
}

const ModalLayoutHeader = ({
  title,
  onClose,
  startSlot,
  endSlot,
  closeButtonProps,
  children,
  sx,
  className,
}) => {
  const {
    onClick: closeButtonOnClick,
    ariaLabel,
    ...restCloseButtonProps
  } = closeButtonProps || {}

  return (
    <Stack
      spacing={children ? 2 : 0}
      sx={{ ...headerContainerSx, ...sx }}
      className={className}
    >
      <Box sx={headerRowSx}>
        <Box sx={headerStartSx}>
          {startSlot || (title ? <Typography variant="h2">{title}</Typography> : null)}
        </Box>
        {endSlot && (
          <Box sx={headerEndSx}>
            {endSlot}
          </Box>
        )}
        <CloseButton
          onClick={closeButtonOnClick || onClose}
          ariaLabel={ariaLabel || (title ? `Close ${title.toLowerCase()}` : 'Close modal')}
          {...restCloseButtonProps}
        />
      </Box>
      {children && (
        <Box>
          {children}
        </Box>
      )}
    </Stack>
  )
}

ModalLayoutHeader.propTypes = {
  title: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  startSlot: PropTypes.node,
  endSlot: PropTypes.node,
  closeButtonProps: PropTypes.object,
  children: PropTypes.node,
  sx: PropTypes.object,
  className: PropTypes.string,
}

ModalLayoutHeader.defaultProps = {
  title: '',
  startSlot: null,
  endSlot: null,
  closeButtonProps: {},
  children: null,
  sx: {},
  className: null,
}

const ModalLayoutContent = ({ children, sx, ...props }) => (
  <Box sx={{ ...contentSx, ...sx }} {...props}>
    {children}
  </Box>
)

ModalLayoutContent.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
}

ModalLayoutContent.defaultProps = {
  children: null,
  sx: {},
}

ModalLayoutRoot.Header = ModalLayoutHeader
ModalLayoutRoot.Content = ModalLayoutContent

export default ModalLayoutRoot
